import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { extractApiErrorMessage } from '@/lib/api-client';
import { useDeletePaymentMutation } from '../api/use-delete-payment-mutation';
import type { InvoiceRow, PaymentRow } from '../types';

const MIN_REASON_LENGTH = 5;

export interface DeletePaymentDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly invoice: InvoiceRow;
  readonly payment: PaymentRow;
  readonly onDeleted?: (() => void) | undefined;
}

interface DeleteFormProps {
  readonly reason: string;
  readonly setReason: (value: string) => void;
  readonly canSubmit: boolean;
  readonly isPending: boolean;
  readonly onCancel: () => void;
  readonly onSubmit: () => void;
}

function DeleteForm({
  reason,
  setReason,
  canSubmit,
  isPending,
  onCancel,
  onSubmit,
}: DeleteFormProps) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) onSubmit();
      }}
      className="space-y-3"
    >
      <div className="flex flex-col gap-1">
        <Label htmlFor="delete_payment_reason">Reason</Label>
        <Textarea
          id="delete_payment_reason"
          required
          minLength={MIN_REASON_LENGTH}
          maxLength={2000}
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
          }}
          disabled={isPending}
          rows={4}
          data-testid="delete-payment-reason"
        />
        <p className="text-muted-foreground text-xs">At least {MIN_REASON_LENGTH} characters.</p>
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel} disabled={isPending}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="destructive"
          disabled={!canSubmit}
          data-testid="delete-payment-confirm"
        >
          {isPending ? 'Deleting…' : 'Delete'}
        </Button>
      </DialogFooter>
    </form>
  );
}

interface DeleteSubmitArgs {
  readonly invoice: InvoiceRow;
  readonly payment: PaymentRow;
  readonly trimmed: string;
  readonly onDeleted: (() => void) | undefined;
  readonly onOpenChange: (open: boolean) => void;
}

function useDeleteSubmit(args: DeleteSubmitArgs) {
  const { invoice, payment, trimmed, onDeleted, onOpenChange } = args;
  const mutation = useDeletePaymentMutation({
    invoiceId: invoice.id,
    orderId: invoice.order_id ?? '',
    blOrderId: invoice.bl_order_id ?? undefined,
  });
  const handleSubmit = () => {
    mutation.mutate(
      { paymentId: payment.id, delete_reason: trimmed },
      {
        onSuccess: () => {
          toast.success('Payment deleted.');
          onDeleted?.();
          onOpenChange(false);
        },
        onError: (err) => {
          toast.error(extractApiErrorMessage(err, 'Failed to delete the payment.'));
        },
      },
    );
  };
  return { mutation, handleSubmit };
}

export function DeletePaymentDialog(props: DeletePaymentDialogProps) {
  const { open, onOpenChange, invoice, payment, onDeleted } = props;
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (open) setReason('');
  }, [open]);
  const trimmed = reason.trim();
  const { mutation, handleSubmit } = useDeleteSubmit({
    invoice,
    payment,
    trimmed,
    onDeleted,
    onOpenChange,
  });
  const canSubmit = trimmed.length >= MIN_REASON_LENGTH && !mutation.isPending;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Delete payment?</DialogTitle>
          <DialogDescription>
            This soft-deletes the payment and walks the invoice status back. Capture why — the
            reason stays on the row.
          </DialogDescription>
        </DialogHeader>
        <DeleteForm
          reason={reason}
          setReason={setReason}
          canSubmit={canSubmit}
          isPending={mutation.isPending}
          onCancel={() => {
            onOpenChange(false);
          }}
          onSubmit={handleSubmit}
        />
      </DialogContent>
    </Dialog>
  );
}
